import { useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft, X, Maximize2, ImageOff } from "lucide-react";
import { fileUrl, imgUrl, imgSrcSet } from "@/lib/api";
import MediaFrame from "@/components/MediaFrame";
import { useLang } from "@/lib/i18n";
import { reportMediaError } from "@/lib/readerEvents";

// Pull every <img> out of an article body → distinct list (first occurrence wins).
export function splitImages(html) {
    const srcs = [];
    const seen = new Set();
    const text = (html || "").replace(/<img\b[^>]*?src="([^"]+)"[^>]*\/?>/gi, (_m, src) => {
        if (!seen.has(src)) { seen.add(src); srcs.push(src); }
        return "";
    }).replace(/<p>\s*<\/p>/gi, "");
    return { text, images: srcs };
}

// Same file referenced under different URLs (absolute vs /api/files/…) → one entry.
export const mediaKey = (u) => String(u || "").split("?")[0].split("/").pop().toLowerCase();
export function uniqueMedia(list) {
    const seen = new Set();
    return list.filter((u) => { if (!u) return false; const k = mediaKey(u); if (seen.has(k)) return false; seen.add(k); return true; });
}

/**
 * Ordered gallery: natural aspect per image (no crop/stretch), space reserved
 * before load, portrait images height-capped, swipe + arrows + keyboard,
 * thumbnails with counter, fullscreen viewer, per-image error state.
 */
export default function ArticleGallery({ images, title, slug }) {
    const { t, lang } = useLang();
    const [i, setI] = useState(0);
    const [open, setOpen] = useState(false);
    const [dims, setDims] = useState({});      // src → {w,h} | "error"
    const [inView, setInView] = useState(false);
    const touch = useRef(null);
    const figRef = useRef(null);
    const n = images.length;
    const go = (d) => setI((x) => (x + d + n) % n);

    useEffect(() => {   // inline gallery counts as "open" while it is on screen
        const el = figRef.current; if (!el) return;
        const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
        io.observe(el);
        return () => io.disconnect();
    }, [n]);

    useEffect(() => {
        if (n < 2 && !open) return;
        if (!open && !inView) return;
        const rtl = lang === "ar";   // RTL: ← = next, → = previous. LTR: the opposite.
        const onKey = (e) => {
            if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
            const tg = e.target;
            if (tg && (tg.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(tg.tagName))) return;
            if (e.key === "Escape") { if (open) setOpen(false); return; }
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            e.preventDefault();
            const next = (e.key === "ArrowLeft") === rtl;
            go(next ? 1 : -1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, inView, n, lang]);

    if (!n) return null;
    const src = images[i];
    const d = dims[src];
    const loaded = d && d !== "error";
    const aspect = loaded ? `${d.w} / ${d.h}` : "16 / 10";
    const portrait = loaded && d.w < d.h;
    const onLoad = (e) => setDims((m) => ({ ...m, [src]: { w: e.target.naturalWidth, h: e.target.naturalHeight } }));
    const onError = () => { setDims((m) => ({ ...m, [src]: "error" })); reportMediaError({ slug, url: src, kind: "image", errorKind: navigator.onLine === false ? "network_offline" : "load_failed" }); };
    const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
    const onTouchEnd = (e) => {
        if (!touch.current || n < 2) return;
        const dx = e.changedTouches[0].clientX - touch.current.x, dy = e.changedTouches[0].clientY - touch.current.y;
        touch.current = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx > 0 ? -1 : 1); // RTL: swipe right → previous
    };

    return (
        <figure ref={figRef} className="my-5" data-testid="article-gallery" data-index={i}>
            <MediaFrame aspect={aspect} portrait={portrait} loaded={!!loaded} className="relative rounded-lg overflow-hidden"
                onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="gallery-frame" data-portrait={portrait ? "1" : "0"}>
                {d === "error" ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-gray-500" data-testid="gallery-image-error">
                        <ImageOff className="w-7 h-7" /> {t("imgFail")}
                    </div>
                ) : (
                    <img key={src} src={imgUrl(src, 960)} srcSet={imgSrcSet(src)} sizes="(min-width: 1024px) 800px, 100vw" alt={title ? `${title} — ${t("imageN", i + 1)}` : t("imageN", i + 1)}
                        className="absolute inset-0 w-full h-full object-contain" loading="eager" decoding="async"
                        onLoad={onLoad} onError={onError} data-testid="gallery-main-image" />
                )}
                <button type="button" onClick={() => setOpen(true)} aria-label={t("viewFull")} className="glow-target absolute top-2 end-2 h-9 w-9 rounded-md bg-black/55 text-white inline-flex items-center justify-center" data-testid="gallery-open"><Maximize2 className="w-4 h-4" /></button>
                {n > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} aria-label={t("prev")} className="glow-target absolute top-1/2 -translate-y-1/2 start-2 h-10 w-10 rounded-full bg-black/55 text-white inline-flex items-center justify-center" data-testid="gallery-prev"><ChevronRight className="w-5 h-5" /></button>
                        <button type="button" onClick={() => go(1)} aria-label={t("next")} className="glow-target absolute top-1/2 -translate-y-1/2 end-2 h-10 w-10 rounded-full bg-black/55 text-white inline-flex items-center justify-center" data-testid="gallery-next"><ChevronLeft className="w-5 h-5" /></button>
                        <span className="absolute bottom-2 start-2 rounded-md bg-black/60 text-white text-xs font-bold px-2 py-1" data-testid="gallery-counter">{i + 1} / {n}</span>
                    </>
                )}
            </MediaFrame>
            {n > 1 && (
                <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                    {images.map((s, k) => (
                        <button key={s} type="button" onClick={() => setI(k)} aria-label={t("imageN", k + 1)} aria-current={k === i}
                            className={`glow-target shrink-0 h-16 w-24 rounded-md overflow-hidden border-2 ${k === i ? "border-brand-red" : "border-transparent opacity-80"}`} data-testid={`gallery-thumb-${k}`}>
                            <img src={imgUrl(s, 320)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                        </button>
                    ))}
                </div>
            )}
            {open && (
                <div role="dialog" aria-modal="true" aria-label={t("gallery")} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setOpen(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="gallery-lightbox">
                    <img src={fileUrl(src)} alt="" className="max-h-[92vh] max-w-[96vw] object-contain" onClick={(e) => e.stopPropagation()} />
                    <a href={fileUrl(src)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="absolute bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 rounded-md bg-white/15 text-white text-xs font-bold px-3 py-2" data-testid="gallery-open-original">{t("openOriginal")}</a>
                    <button type="button" onClick={() => setOpen(false)} aria-label={t("close")} className="absolute top-3 end-3 h-11 w-11 rounded-full bg-white/15 text-white inline-flex items-center justify-center" data-testid="gallery-close"><X className="w-5 h-5" /></button>
                    {n > 1 && (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label={t("prev")} className="absolute top-1/2 -translate-y-1/2 start-3 h-12 w-12 rounded-full bg-white/15 text-white inline-flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label={t("next")} className="absolute top-1/2 -translate-y-1/2 end-3 h-12 w-12 rounded-full bg-white/15 text-white inline-flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
                            <span className="absolute top-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 text-white text-sm font-bold">{i + 1} / {n}</span>
                        </>
                    )}
                </div>
            )}
        </figure>
    );
}
